import { useEffect, useId, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router";
import type { ActorKind, ActorRef, Comment, PetPublicProfile, Post } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { useSessionState } from "@/shared/session/onboarding";
import { useActiveHome, useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { Button, Card, Chip, DataOriginBadge, EMPTY_ART, EmptyState, ErrorState, Icon, Page, PetAvatar, PlanetEventLine, QueryView, ToggleChip, TopBar } from "@/shared/ui";
import { collapseRepeats } from "./feed";
import { PostPhotos } from "./PostPhotos";
import "./social.css";

/** 当前查看者的身份：自己的宠物与账号（用于判断“这是我发的吗”）。 */
function useViewer() {
  const home = useActiveHome({ staleTime: 60_000 });
  const session = useSessionState();
  return { homeId: home.data?.home_id ?? null, petId: home.data?.pet.pet_id ?? null, petName: home.data?.pet.name ?? "TA", userId: session.data?.user?.user_id ?? null };
}

function isMine(actor: ActorRef, viewer: ReturnType<typeof useViewer>): boolean {
  return (actor.actor_kind === "pet" && actor.actor_id === viewer.petId) || (actor.actor_kind === "owner" && actor.actor_id === viewer.userId);
}

function ActorLabel({ actor }: { actor: ActorRef }) {
  const tag = actor.actor_kind === "npc" ? <Chip tone="sky">星球居民</Chip> : actor.actor_kind === "owner" ? <Chip tone="sun">主人</Chip> : null;
  const name = actor.actor_kind === "pet" ? <Link to={`/pets/${actor.actor_id}`}>{actor.display_name}</Link> : <span>{actor.display_name}</span>;
  return (
    <span className="ps-actor">
      <strong>{name}</strong>
      {tag}
    </span>
  );
}

/**
 * 举报 / 屏蔽 / 撤下：都需要主人明确点选；屏蔽后双方互相看不到。
 * 撤下自己的动态先问一句：撤下后不能恢复（S-056，PRIVACY-COPY-01）。原按钮留在原位（拿掉会让焦点掉到页面上、菜单跟着关），确认那一行加在下面。
 */
function ModerationMenu({ kind, id, mine, onRemoved }: { kind: "post" | "comment"; id: string; mine: boolean; onRemoved?: () => void }) {
  const { social } = useServices();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const groupId = useId();
  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!open || done) return;
    const outside = (event: PointerEvent) => {
      const current = root.current;
      if (current?.isConnected && event.target instanceof Node && !current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [open, done]);
  // 菜单收起时确认那一行也收起，下次打开从头问
  useEffect(() => {
    if (!open) setConfirming(false);
  }, [open]);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["social"] });
  const remove = useMutation({
    mutationFn: () => (kind === "post" ? social.removePost(id) : social.removeComment(id)),
    onSuccess: () => {
      setDone(kind === "post" ? "已撤下" : "已删除");
      refresh();
      onRemoved?.();
    },
  });
  const report = useMutation({ mutationFn: () => social.report(kind, id, "owner_reported"), onSuccess: () => setDone("已举报，我们会查看") });
  const block = useMutation({
    mutationFn: () => social.block(kind === "post" ? { post_id: id } : { comment_id: id }),
    onSuccess: () => {
      setDone("已屏蔽这位作者");
      refresh();
    },
  });
  const error = remove.error ?? report.error ?? block.error;
  if (done) return <span className="ps-muted" role="status">{done}</span>;
  return (
    <span
      ref={root}
      className="ps-mod"
      onBlur={(event) => {
        if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open || !event.currentTarget.isConnected || !event.currentTarget.contains(event.target as Node)) return;
        event.stopPropagation();
        setOpen(false);
        const current = trigger.current;
        if (current?.isConnected && !current.disabled && event.currentTarget.contains(current)) current.focus();
      }}
    >
      <Button size="sm" variant="ghost" aria-expanded={open} aria-controls={groupId} aria-label="更多操作" onClick={(event) => { trigger.current = event.currentTarget; setOpen((v) => !v); }}>
        ···
      </Button>
      {open ? (
        <span id={groupId} className="ps-mod__menu" role="group" aria-label="更多操作">
          {mine && kind === "post" ? (
            <>
              <Button size="sm" variant="danger" data-mod-remove="" aria-expanded={confirming} onClick={() => setConfirming((v) => !v)}>
                撤下这条动态
              </Button>
              {confirming ? (
                <span className="ps-mod__confirm" role="group" aria-label="确认撤下">
                  <span className="ps-mod__confirm-text">撤下后不能恢复，别人也看不到这条了。</span>
                  <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
                    确定撤下
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={remove.isPending}
                    onClick={() => {
                      root.current?.querySelector<HTMLButtonElement>("[data-mod-remove]")?.focus();
                      setConfirming(false);
                    }}
                  >
                    不撤了
                  </Button>
                </span>
              ) : null}
            </>
          ) : mine ? (
            <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
              删除留言
            </Button>
          ) : (
            <>
              <Button size="sm" variant="secondary" loading={report.isPending} onClick={() => report.mutate()}>
                举报
              </Button>
              <Button size="sm" variant="secondary" loading={block.isPending} onClick={() => block.mutate()}>
                屏蔽作者
              </Button>
            </>
          )}
        </span>
      ) : null}
      {error ? (
        <span role="alert" className="ps-muted" style={{ color: "color-mix(in srgb, var(--c-danger) 75%, var(--c-ink))" }}>
          {toApiError(error).playerMessage}
        </span>
      ) : null}
    </span>
  );
}

function PostCard({ post, linkToThread = true }: { post: Post; linkToThread?: boolean }) {
  const { social } = useServices();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const viewer = useViewer();
  const keyRef = useRef(newIdempotencyKey("react"));
  const react = useMutation({
    mutationFn: () => social.react(post.post_id, { as_actor: "pet" }, keyRef.current),
    onSuccess: (next) => {
      keyRef.current = newIdempotencyKey("react");
      queryClient.setQueryData(queryKeys.post(post.post_id), next);
      void queryClient.invalidateQueries({ queryKey: queryKeys.circleFeed });
    },
  });
  const mine = isMine(post.author, viewer);
  return (
    <Card className="ps-post">
      <div className="ps-row" style={{ justifyContent: "space-between" }}>
        <div className="ps-row" style={{ minWidth: 0 }}>
          {/* 作者（ActorRef）不带物种、也不带来历：不编一个物种（原来写死“猫”），没照片时就是爪印 */}
          <PetAvatar petId={post.author.actor_id} name={post.author.display_name} photoUrl={post.author.avatar_url} size={36} />
          <ActorLabel actor={post.author} />
        </div>
        <ModerationMenu kind="post" id={post.post_id} mine={mine} onRemoved={() => (!linkToThread ? navigate("/circle") : undefined)} />
      </div>
      <p className="ps-post__text">{post.text}</p>
      <PlanetEventLine event={post.planet_event} />
      {/* 照片（SOCIAL-MULTIPHOTO-117）：1–9 张宫格，点开逐张看整张；排在正文和星球活动那一行后面 */}
      <PostPhotos post={post} viewer={viewer} />
      <div className="ps-row" style={{ justifyContent: "space-between" }}>
        <div className="ps-row">
          {/* 点赞（UI-BASE-01b，W-12）：不能点的两种（已经点过、自己的动态）一个样子——透明按钮的禁用态、赞数用 --c-ink-2；点过的那颗心是实心的 */}
          <Button
            size="sm"
            variant="ghost"
            className={post.viewer_reacted ? "ps-post__like is-reacted" : "ps-post__like"}
            icon="heart"
            loading={react.isPending}
            disabled={post.viewer_reacted || mine}
            onClick={() => react.mutate()}
            aria-label={post.viewer_reacted ? `${viewer.petName} 已点赞，共 ${post.reaction_count}` : `以 ${viewer.petName} 的身份点赞，当前 ${post.reaction_count}`}
          >
            {post.reaction_count}
          </Button>
          {linkToThread ? (
            <Link className="ps-btn ps-btn--ghost ps-btn--sm" to={`/posts/${post.post_id}`} aria-label={`查看评论，共 ${post.comment_count} 条`}>
              <Icon name="comment" size={16} /> {post.comment_count}
            </Link>
          ) : null}
        </div>
        <DataOriginBadge origin={post.data_origin} />
      </div>
      {react.isError ? (
        <span role="alert" className="ps-muted" style={{ color: "color-mix(in srgb, var(--c-danger) 75%, var(--c-ink))" }}>
          {toApiError(react.error).playerMessage}
        </span>
      ) : null}
    </Card>
  );
}

export function CirclePage() {
  const { social } = useServices();
  const query = useQuery({ queryKey: queryKeys.circleFeed, queryFn: () => social.feed() });
  return (
    <Page>
      {/* 原“星球”标签退役后，这里从通讯器的“朋友圈”入口进来；返回回到通讯器（TA 的朋友那一栏，和入口同一排）。 */}
      <TopBar title="朋友圈" subtitle="宠物们自己的公开动态" back="/communicator?channel=phone" />
      {/* “星球上的地方 · 爪爪驾校”（circle.places 插槽）属于已退役的“星球”标签，这里不再渲染（地图首页方案 v2.1 第 8.1 节）：
          TA 想学开车、学车进度出现在地图主状态面板；主人主动的入口在证件卡包的驾照卡位。 */}
      <QueryView query={query} isEmpty={(p) => p.items.length === 0} empty={<EmptyState icon="planet" art={EMPTY_ART.circle} title="还没有动态">宠物们出门到访后的真实事件会出现在这里。</EmptyState>}>
        {(page) => (
          <div className="ps-stack">
            {/* 老数据里同一作者一字不差、没带照片的几条只留最新一条（feed.ts；新的重复后端已经挡住） */}
            {collapseRepeats(page.items).map((post) => (
              <PostCard key={post.post_id} post={post} />
            ))}
          </div>
        )}
      </QueryView>
    </Page>
  );
}

/** 评论：主人明确选择以“宠物”还是“主人自己”的身份说话；不会替主人自动发言。 */
function Composer({ postId, replyTo, onDone }: { postId: string; replyTo: Comment | null; onDone: () => void }) {
  const { social } = useServices();
  const queryClient = useQueryClient();
  const viewer = useViewer();
  const [text, setText] = useState("");
  const [asActor, setAsActor] = useState<ActorKind>("pet");
  const keyRef = useRef(newIdempotencyKey("comment"));
  const send = useMutation({
    mutationFn: () => social.comment(postId, { as_actor: asActor, text: text.trim(), reply_to_comment_id: replyTo?.comment_id ?? null }, keyRef.current),
    onSuccess: () => {
      keyRef.current = newIdempotencyKey("comment");
      setText("");
      onDone();
      void queryClient.invalidateQueries({ queryKey: queryKeys.post(postId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.circleFeed });
    },
  });
  return (
    <form
      className="ps-stack ps-composer-card"
      onSubmit={(e) => {
        e.preventDefault();
        if (text.trim()) send.mutate();
      }}
    >
      <div className="ps-segmented" role="group" aria-label="以谁的身份留言">
        <ToggleChip pressed={asActor === "pet"} onToggle={() => setAsActor("pet")}>
          以 {viewer.petName} 的身份
        </ToggleChip>
        <ToggleChip pressed={asActor === "owner"} onToggle={() => setAsActor("owner")}>
          以主人身份
        </ToggleChip>
      </div>
      {replyTo ? <span className="ps-muted">回复 {replyTo.actor.display_name}</span> : null}
      <label className="visually-hidden" htmlFor={`comment-${postId}`}>
        留言
      </label>
      <textarea id={`comment-${postId}`} className="ps-textarea" maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder="说点什么……" />
      <div className="ps-row">
        <Button type="submit" variant="primary" size="sm" loading={send.isPending} disabled={!text.trim()}>
          发表
        </Button>
        {replyTo ? (
          <Button size="sm" variant="ghost" onClick={onDone}>
            取消回复
          </Button>
        ) : null}
        {send.isError ? (
          <span role="alert" className="ps-muted" style={{ color: "color-mix(in srgb, var(--c-danger) 75%, var(--c-ink))" }}>
            {toApiError(send.error).playerMessage}（内容还在）
          </span>
        ) : null}
      </div>
    </form>
  );
}

export function PostThreadPage() {
  const { postId = "" } = useParams();
  const { social } = useServices();
  const viewer = useViewer();
  const [replyTo, setReplyTo] = useState<Comment | null>(null);
  const post = useQuery({ queryKey: queryKeys.post(postId), queryFn: () => social.post(postId) });
  const comments = useQuery({ queryKey: [...queryKeys.post(postId), "comments"], queryFn: () => social.comments(postId) });
  return (
    <Page>
      <TopBar title="动态" back="/circle" />
      <QueryView query={post}>{(p) => <PostCard post={p} linkToThread={false} />}</QueryView>
      <div className="ps-section-title">评论</div>
      <QueryView query={comments} isEmpty={(c) => c.items.length === 0} empty={<EmptyState icon="comment" title="还没有评论" />}>
        {(page) => (
          <ul className="ps-comments">
            {page.items.map((c) => (
              <li key={c.comment_id} className={c.reply_to_comment_id ? "is-reply" : undefined}>
                <div className="ps-row" style={{ justifyContent: "space-between" }}>
                  <ActorLabel actor={c.actor} />
                  {!c.removed && c.actor.actor_kind !== "npc" ? <ModerationMenu kind="comment" id={c.comment_id} mine={isMine(c.actor, viewer)} /> : null}
                </div>
                <div>{c.removed ? <em className="ps-muted">这条留言已删除</em> : c.text}</div>
                {!c.removed && env.dataMode === "live" ? (
                  <button type="button" className="ps-link-btn" onClick={() => setReplyTo(c)}>
                    回复
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </QueryView>
      {env.dataMode === "live" && post.data ? <Composer postId={postId} replyTo={replyTo} onDone={() => setReplyTo(null)} /> : null}
    </Page>
  );
}

/**
 * 关注、取消关注用自家哪只宠物的身份（POST /pets/{id}/follow?as_pet_id=…；FOLLOW-AS-PET-01，I 10-09 定）。只在这一处决定，以后换判断只改这里：
 * - 关注：一律用 HouseholdProvider 当前选中的那只；不靠“家里只有一只就可以省略”（后端好几只没带回 403）。
 * - 取消：本该用“当初是哪只关注的”那只，可 viewer_follows 只说家里任一只关注了、不说是哪只，过渡期也用当前选中那只（取消后重读兜底，见 FollowButton）。
 *   CONTRACT-SYNC-I109 接上 viewer_following_pet_ids 以后按钮状态按当前那只显示，这里的判断跟着换（follow 这个参数就是留给它的）。
 * - 没有家庭上下文、或上下文里没有当前宠物：null（页面不放按钮、不发请求）。
 */
export function followAsPetId(current: { pet: { pet_id: string } | null } | null, _follow: boolean): string | null {
  const petId = current?.pet?.pet_id;
  return typeof petId === "string" && petId !== "" ? petId : null;
}

/**
 * 取消关注以后重读一次：只重读页面挂着的 queryKeys.petProfile(petId) 那一份（完整主页挂的就是这个键），只看 viewer_follows 一个字段，不认回包的其余形状。
 * 没挂着、没真的重读到、读失败、回包里没有这一项，都当“不知道”（null），不下结论。
 */
async function stillFollowing(queryClient: QueryClient, petId: string): Promise<boolean | null> {
  const key = queryKeys.petProfile(petId);
  const before = queryClient.getQueryState(key)?.dataUpdateCount ?? 0;
  try {
    await queryClient.refetchQueries({ queryKey: key, exact: true }, { throwOnError: true });
  } catch {
    return null;
  }
  const state = queryClient.getQueryState(key);
  if (!state || state.dataUpdateCount === before) return null;
  const value = (state.data as { viewer_follows?: unknown } | undefined)?.viewer_follows;
  return typeof value === "boolean" ? value : null;
}

/**
 * 关注按钮：只吃 petId、following 两个属性（完整主页和以后“仅关注者”档的最小名片共用，不和完整主页的回包形状绑死）。
 * - live：以当前选中那只的身份关注、取消（followAsPetId）。没有当前宠物就不放按钮，原位一句“有了自己的宠物，就能关注 TA”——
 *   不放置灰按钮：置灰的点下去还是 403。
 * - 取消关注的过渡期兜底（I 10-09 定）：取消以后重读一次；还显示关注着，说明当前这只本来就没关注过（是家里别的那只关注的），
 *   不算成功，照实说“<当前那只的名字>没关注过 TA”（例如“豆沙没关注过 TA”；这页没有切换栏，写“这只”主人看不出是哪只），按钮维持原状。
 *   名字和宠物号从同一处取（当前选中那只）。CONTRACT-SYNC-I109 接上 viewer_following_pet_ids 以后这段兜底跟着撤。
 * - 演示模式：不认当前宠物（演示服务不看 as_pet_id），也不做重读兜底，照旧。
 */
export function FollowButton({ petId, following }: { petId: string; following: boolean }) {
  const { social } = useServices();
  const queryClient = useQueryClient();
  const current = useOptionalCurrentHousehold();
  const live = env.dataMode === "live";
  const asPetId = followAsPetId(current, !following);
  const toggle = useMutation({
    mutationFn: async (): Promise<{ notFollowedBy: string | null }> => {
      await social.follow(petId, !following, asPetId);
      const still = live && following && asPetId ? await stillFollowing(queryClient, petId) : null;
      if (still === null) void queryClient.invalidateQueries({ queryKey: queryKeys.petProfile(petId) });
      return { notFollowedBy: still === true ? asPetId : null };
    },
  });
  if (live && !asPetId) return <span className="ps-muted">有了自己的宠物，就能关注 TA</span>;
  return (
    <>
      <Button size="sm" variant={following ? "secondary" : "primary"} loading={toggle.isPending} onClick={() => toggle.mutate()}>
        {following ? "已关注" : "关注"}
      </Button>
      {following && asPetId && toggle.data?.notFollowedBy === asPetId ? <span className="ps-muted" role="status">{current?.pet?.name}没关注过 TA</span> : null}
      {toggle.isError ? <ErrorState error={toggle.error} /> : null}
    </>
  );
}

/**
 * 宠物主页上的来历小牌：按看的人和这只宠物的关系写（08a9 10-03 白天走查）。
 * 后端给的 origin_label 是固定一句（own_pet 写“自己的宠物”），别家的人看了容易读成“我的”：
 * - 自己家的宠物（is_own）：照旧用后端那句；
 * - 别家的宠物：按 origin 写成从旁人看的说法；没给 origin（旧接口）就不挂这块牌子，不拿那句去猜。
 */
const OTHERS_ORIGIN: Record<string, string> = {
  own_pet: "家里养的宠物",
  adopted_original: "PetSoul 原创伙伴",
  adopted_real_archive: "真实原型纪念档案",
};
export function profileOriginLabel(p: Pick<PetPublicProfile, "is_own" | "origin" | "origin_label">): string | null {
  if (p.is_own) return p.origin_label ?? null;
  return p.origin && Object.prototype.hasOwnProperty.call(OTHERS_ORIGIN, p.origin) ? OTHERS_ORIGIN[p.origin] : null;
}

export function PetProfilePage() {
  const { petId = "" } = useParams();
  const { pets, social } = useServices();
  const profile = useQuery({ queryKey: queryKeys.petProfile(petId), queryFn: () => pets.publicProfile(petId) });
  const posts = useQuery({ queryKey: queryKeys.petPosts(petId), queryFn: () => social.petPosts(petId) });
  return (
    <Page>
      <TopBar title="宠物主页" back />
      <QueryView query={profile}>
        {(p) => (
          <Card className="ps-row" style={{ alignItems: "flex-start" }}>
            {/* 公开主页（PetPublicProfile）有物种、没有来历（origin_label 只是一句说明文字）：不按它猜来历，没照片时是爪印 */}
            <PetAvatar petId={p.pet_id} name={p.display_name} species={p.species} photoUrl={p.avatar_url} size={64} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2 className="ps-h2">{p.display_name}</h2>
              <div className="ps-muted">{p.bio ?? "还没有简介"}</div>
              <div className="ps-row" style={{ flexWrap: "wrap" }}>
                <Chip>{p.follower_count} 只宠物关注</Chip>
                <Chip>{p.post_count} 动态</Chip>
                {profileOriginLabel(p) ? <Chip>{profileOriginLabel(p)}</Chip> : null}
                <DataOriginBadge origin={p.data_origin} />
              </div>
            </div>
            {!p.is_own ? <FollowButton petId={p.pet_id} following={p.viewer_follows} /> : <Chip>我的伙伴</Chip>}
          </Card>
        )}
      </QueryView>
      <div className="ps-section-title">TA 的动态</div>
      <QueryView query={posts} isEmpty={(p) => p.items.length === 0} empty={<EmptyState title="还没有公开动态" />}>
        {(page) => (
          <div className="ps-stack">
            {/* 同一作者、一字不差、都没照片的老数据只留最新一条（与朋友圈同一个兜底）。 */}
            {collapseRepeats(page.items).map((post) => (
              <PostCard key={post.post_id} post={post} />
            ))}
          </div>
        )}
      </QueryView>
    </Page>
  );
}
